import uniq from 'lodash/uniq';

export const MEEOPP_ORG_ID = '5b909bc2f5bace46af3fbae0';
export const REQUIRED_RULE = {
  required: true,
  message: 'This field is required!',
};

const CHINESE_ENGLISH_PUNCTUATION_MESSAGE = (language) =>
  `Please include ${language} punctuation in the sentence!` +
  ' ' +
  'Please also keep in mind that Chinese punctuation is different ' +
  'from English punctuation (ie: 。 vs .)';

export const ENGLISH_PUNCTUATION_REQUIRED_RULE = {
  pattern: /[.?!]/gm,
  message: CHINESE_ENGLISH_PUNCTUATION_MESSAGE('ENGLISH'),
};

export const CHINESE_PUNCTUATION_REQUIRED_RULE = {
  pattern: /[。！？·]/gm,
  message: CHINESE_ENGLISH_PUNCTUATION_MESSAGE('CHINESE'),
};

export const CORE_CONCEPT_TYPES = Object.freeze({
  grammarConcept: 'GRAMMAR_CONCEPT',
  literaryDevice: 'LITERARY_DEVICE',
  languageFunction: 'LANGUAGE_FUNCTION',
});

export const KEYS = {
  fetchOrganizations: 'fetchOrganizations',
  fetchOrganization: 'fetchOrganization',
  submittingOrganization: 'submittingOrganization',
  fetchUserVocabs: 'fetchUserVocabs',
  fetchPastUserLessons: 'fetchPastUserLessons',
  fetchTopicSlides: 'fetchTopicSlides',
  fetchTopicAudioClips: 'fetchTopicAudioClips',
  fetchTopicQuestions: 'fetchTopicQuestions',
  fetchQuiz: 'fetchQuiz',
  fetchQuestionTags: 'fetchQuestionTags',
  questionTags: 'questionTags',
  updateQuestionTag: 'updateQuestionTag',
  addQuestiontag: 'addQuestiontag',
  fetchStudentPreferenceInstructor: 'fetchStudentPreferenceInstructor',
  fetchOrganizationBookingLesson: 'fetchOrganizationBookingLesson',
  orgAdmins: 'orgAdmins',
  createTopicTag: 'createTopicTag',
  getTopic: 'getTopic',
  fetchPreviewOrgDetails: 'fetchPreviewOrgDetails',
  fetchPreviewUserDetails: 'fetchPreviewUserDetails',
  getMostRecentOralScore: 'getMostRecentOralScore',
  orgBookingAlert: 'orgBookingAlert',
  fetchRecentQuizScoresByType: 'fetchRecentQuizScoresByType',
  getUser: 'getUser',
  fetchOrgBookingOralScores: 'fetchOrgBookingOralScores',
  getOrgCurriculum: 'getOrgCurriculum',
  getLastWritingPad: 'getLastWritingPad',
  fetchSchoolClass: 'fetchSchoolClass',
  fetchStaffSchoolClasses: 'fetchStaffSchoolClasses',
  fetchInstructorsMonthlyReport: 'fetchInstructorsMonthlyReport',
  fetchSchoolClasses: 'fetchSchoolClasses',
  clearSchoolClassState: 'clearSchoolClassState',
  getOrganizationBookingOptions: 'getOrganizationBookingOptions',
  fetchTopicByTitle: 'fetchTopicByTitle',
  patchSchoolClass: 'patchSchoolClass',
};

export const PAGINATION_TYPES = {
  pendingLessons: 'pendingLessons',
  userLessons: 'userLessons',
  pastLessons: 'pastLessons',
  upcomingLessons: 'upcomingLessons',
  liveLessons: 'liveLessons',
  cancelledLessons: 'cancelledLessons',
  libraryQuizzes: 'libraryQuizzes',
  bookingOptions: 'bookingOptions',
  orgBooking: 'orgBooking',
  bookingRequests: 'bookingRequests',
  orgStudents: 'orgStudents',
  users: 'users',
  instructorReviews: 'instructorReviews',
  organizations: 'organizations',
  topics: 'topics',
  instructorsWorkLogs: 'instructorsWorkLogs',
  schoolClasses: 'schoolClasses',
  sentenceFlashcards: 'sentenceFlashcards',
  word: 'coreWord',
  studentsThatLikeInstructor: 'studentsThatLikeInstructor',
  studentsThatDislikeInstructor: 'studentsThatDislikeInstructor',
  fetchTopicByTitle: 'fetchTopicByTitle',
  avatarSets: 'avatarSets',
  pointsByClass: 'pointsByClass',
  pointsByOrganization: 'pointsByOrganization',
};

export const LESSON_COLUMNS = {
  student: 'student',
  practiceBuddy: 'practiceBuddy',
  minutes: 'minutes',
  timeSpent: 'timeSpent',
  groupBooking: 'groupBooking',
  scheduledAt: 'scheduledAt',
  status: 'status',
  topic: 'topic',
  userType: 'userType',
  startDate: 'startDate',
  endDate: 'endDate',
  lessonStatus: 'lessonStatus',
  topicName: 'topicName',
  deadline: 'deadline',
  showAt: 'showAt',
};

export const USER_COLUMNS = {
  name: 'name',
  title: 'title',
  email: 'email',
  organization: 'organization',
  signedUpOn: 'signedUpOn',
  userType: 'userType',
  action: 'action',
  instructorStatus: 'instructorStatus',
};

export const POINTS = {
  1: 3,
  2: 2,
  3: 1,
  4: 0,
};

export const QUESTIONS_TYPES = {
  1: 'Multiple Choice',
  2: 'Multi Select',
  3: 'Short Question',
};

export const PAGINATION_LIMIT = 30;
export const PAGINATION_LIMIT_SHORT = 10;
export const REQUEST_LIMIT = 500;
export const CONTENT_LIBRARY_PAGE_SIZE = 24;

export const SEARCH_DEBOUNCE_DELAY_MS = 300;

export const LESSON_STATUS = {
  CANCELLED: 'Cancelled',
  DONE: 'Done',
  NO_SHOW: 'No Show',
  PENDING: 'Pending',
  SCHEDULED: 'Scheduled',
  IN_PROGRESS: 'In Progress',
  REVIEWED: 'Done',
  EXPIRED: 'Expired',
};

export const LESSON_CANCELLATION_TYPES_DICT = {
  NO_SHOW: 'No Show',

  // A cancellation more than 24 hours before the lesson
  NORMAL: 'Cancelled more than 24h before',

  // Late Policy 1 (as of Feb 2020): we differentiate between late cancellations
  // made between 12 and 24 hours before the lesson and between 0 and 12 hours
  // before the lesson.
  'LATE.LP1.12-24': 'Cancelled less than 24h before',
  'LATE.LP1.00-12': 'Cancelled less than 12h before',

  TECH_ISSUE: 'Tech Issue',
};

export const LESSON_CANCELLATION_REASON_TEXT = {
  ADMIN_BOOKING_DETAILS: 'Cancelled from meeopp.net (via admin booking details)',
  ADMIN_OOO_LESSON: 'Cancelled from meeopp.net (via admin OOO lesson page)',
  ORG_BOOKING_DETAILS: 'Cancelled from meeopp.net (via org booking detail)',
};

export const CLOUDINARY_UPLOAD_PRESET = 'yd1z9dlx';
export const CLOUDINARY_UPLOAD_PRESET_AR = 'mpewlw5d'; //aspect ratio 16:9
export const CLOUDINARY_UPLOAD_URL = 'https://api.cloudinary.com/v1_1/meeup/upload';

export const WEEK_DAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

export const slotsToShow = {
  custom: {
    label: 'Individual Times',
    description: 'Enter individual booking times',
  },
  all: {
    label: 'Time Range',
    description: 'Enter a range of available times',
  },
  none: {
    label: 'None',
    description: 'Bookings will be disabled on this day',
  },
};

export const NOTE_TYPE = {
  learningObjective: 'LearningObjective',
  noteForPracticeBuddy: 'NoteForPracticeBuddy',
  studentLevel: 'StudentLevel',
  warning: 'Warning',
  oralGrade: 'OralGrade',
  writingGrade: 'WritingGrade',
  readingGrade: 'ReadingGrade',
  listeningGrade: 'ListeningGrade',
  targetGrade: 'TargetGrade',
};

export const STUDENT_LEVEL = {
  WEAK: 'Weak',
  AVERAGE: 'Average',
  ADVANCED: 'Advanced',
  VERY_ADVANCED: 'Very advanced',
};

export const STUDENT_WARNING = {
  STUDENT_IS_STRESSED: 'Student is stressed',
  NEEDS_MORE_TIME_TO_ANSWER_QUESTIONS: 'Needs more time to answer questions',
};

export const INSTRUCTOR_STATUS = {
  PENDING: 'Pending',
  HIRING: 'Hiring',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  SUSPENDED: 'Suspended',
};

export const LESSON_DURATION = [
  { value: '25', label: '25 minutes', key: 1 },
  { value: '30', label: '30 minutes', key: 1 },
  { value: '45', label: '45 minutes', key: 2 },
  { value: '50', label: '50 minutes', key: 2 },
  { value: '60', label: '60 minutes', key: 3 },
  { value: '75', label: '75 minutes', key: 3 },
  { value: '100', label: '100 minutes', key: 3 },
];

export const VIDEO_RESOLUTION = ['1280x720', '640x480', '320x240'];
export const VIDEO_FRAME_RATE = [30, 15];

export const NOTIFICATIONS_CHANNELS = [{ label: 'Silently join', key: 'silently-join' }];

export const LESSON_TYPES = {
  CHALLENGE_REVIEW: 'CHALLENGE_REVIEW',
  SOLO: 'SOLO',
  DEFAULT: 'DEFAULT',
  WGT: 'WGT',
  CORE_ITEMS_IWGT: 'CORE_ITEMS_IWGT',
  QUIZ: 'QUIZ',
  PLAYBOOK: 'PLAYBOOK',
};

export const LESSON_TYPE_NAMES = {
  CHALLENGE_REVIEW: 'Challenge Review',
  SOLO: '1-1',
  DEFAULT: '1-on-1',
  WGT: 'Walking Guided Tour ',
  CORE_ITEMS_IWGT: 'Vocab Packs',
  QUIZ: 'Challenge',
  PLAYBOOK: 'Playbook',
  ASSESSMENT: 'Challenge - Assessment',
};

export const COLUMN_TYPES = {
  lesson: 'lesson',
  lessonTeacherView: 'lessonTeacherView',
  user: 'user',
};

export const QUESTION_TYPES_MAP = Object.freeze({
  short: 'SHORT',
  multiChoice: 'MULTI_CHOICE',
  multiChoiceText: 'MULTI_CHOICE_TEXT',
  multiChoiceImage: 'MULTI_CHOICE_IMAGE',
  multiAnswer: 'MULTI_ANSWER',
  multiAnswerAlphabet: 'MULTI_ANSWER_ALPHABET',
  spoken: 'SPOKEN',
  sentenceSort: 'SENTENCE_SORT',
  matching: 'MATCHING_PARENT',
  matchingChild: 'MATCHING_CHILD',
  table: 'TABLE_PARENT',
  tableChild: 'TABLE_CHILD',
  tableMulti: 'TABLE_MULTI_PARENT',
  tableMultiRow: 'TABLE_MULTI_ROW_PARENT',
  tableMultiRowChild: 'TABLE_MULTI_ROW_CHILD',
  multiChoicePlaybook: 'MULTI_CHOICE_PLAYBOOK',
  multiAnswerPlaybook: 'MULTI_ANSWER_PLAYBOOK',
  openEndedOral: 'OPEN_ENDED_ORAL',
  openEndedWriting: 'OPEN_ENDED_WRITING',
});

export const OPEN_ENDED_QUESTION_TYPES = [
  QUESTION_TYPES_MAP.openEndedOral,
  QUESTION_TYPES_MAP.openEndedWriting,
];

export const CDNIS_QUESTION_TYPES = [
  QUESTION_TYPES_MAP.multiChoiceText,
  QUESTION_TYPES_MAP.multiChoiceImage,
];

export const AUTO_GRADED_QUESTIONS = Object.freeze([
  QUESTION_TYPES_MAP.short,
  QUESTION_TYPES_MAP.spoken,
]);

export const PARENT_QUESTION_TYPES = [
  QUESTION_TYPES_MAP.table,
  QUESTION_TYPES_MAP.matching,
  QUESTION_TYPES_MAP.tableMulti,
  QUESTION_TYPES_MAP.tableMultiRow,
];

export const SHORT_ANSWER_TYPES_MAP = Object.freeze({
  oneLine: 'ONE_LINE',
  trueFalse: 'TRUE_FALSE',
});

export const CLOUDINARY_UPLOAD_ACTION = `${CLOUDINARY_UPLOAD_URL}?upload_preset=${CLOUDINARY_UPLOAD_PRESET}`;

export const QUESTION_INITIAL_VALUES = {
  answerExplanationWithPinyin: undefined,
  questionData: {
    options: [],
    correctOptions: [],
  },
  media: { url: '', type: '', transcript: '' },
  childQuestions: [],
  childQuestionIds: [],
  shortAnswerType: '',
  example: null,
};

export const USER_TYPES_MAP = Object.freeze({
  student: 'STUDENT',
  instructor: 'INSTRUCTOR',
  schoolStaff: 'SCHOOL_STAFF',
  meeoppStaff: 'MEEOPP_STAFF',
});

export const USER_TYPES_LABELS = {
  [USER_TYPES_MAP.student]: 'Student',
  [USER_TYPES_MAP.instructor]: 'Instructor',
  [USER_TYPES_MAP.schoolStaff]: 'School Staff',
  [USER_TYPES_MAP.meeoppStaff]: 'MeeOpp Staff',
};

export const USER_TYPES_WITH_PREFERRED_LOCALE = Object.freeze([
  USER_TYPES_MAP.schoolStaff,
  USER_TYPES_MAP.meeoppStaff,
]);

export const ACCEPTABLE_CONTACT_METHODS = Object.freeze({
  PHONE: 'Phone (Voice Call)',
  SMS: 'Phone (SMS)',
  WHATSAPP: 'Whatsapp',
  WECHAT: 'Wechat',
  EMAIL: 'Email',
});

export const CONTENT_TYPE = Object.freeze({
  topic: 'TOPIC',
  quiz: 'QUIZ',
  presentation: 'PRESENTATION',
  assessment: 'ASSESSMENT',
  corePack: 'CORE_PACK',
  playbook: 'PLAYBOOK',
});

export const CONTENT_TAG_CATEGORIES_MAP = Object.freeze({
  curriculum: 'CURRICULUM',
  curriculumLevel: 'CURRICULUM_LEVEL',
  curriculumLevelTwo: 'CURRICULUM_LEVEL_TWO',
  theme: 'THEME',
  subtheme: 'SUBTHEME',
  focus: 'FOCUS',
  topic: 'TOPIC',
  misc: 'MISCELLANEOUS',
  difficulty: 'DIFFICULTY',
  type: 'TYPE',
  deprecated: 'DEPRECATED',
  meeoppTopic: 'MEEOPP_TOPIC',
  meeoppSubtopic: 'MEEOPP_SUBTOPIC',
  meeoppCycleExerciseType: 'MEEOPP_CYCLE_EXERCISE_TYPE',
  learningFocus: 'LEARNING_FOCUS',
  metadata: 'METADATA',
  questionTopic: 'QUESTION_TOPIC',
  questionCustom: 'QUESTION_CUSTOM',
  questionMainTag1: 'QUESTION_MAIN_TAG_1',
  skillArea: 'SKILL_AREA',
  questionDifficulty: 'QUESTION_DIFFICULTY',
  questionResponseFormat: 'QUESTION_RESPONSE_FORMAT',
  questionExpressionMethod: 'QUESTION_EXPRESSION_METHOD',
  relationKey: 'RELATION_KEY',
});

export const CONTENT_TAG_STATUS_MAP = Object.freeze({
  published: 'PUBLISHED',
  draft: 'DRAFT',
  deprecated: 'DEPRECATED',
});

export const CONTENT_TAGS_SORT_WEIGHTING = {
  [CONTENT_TAG_CATEGORIES_MAP.focus]: 10,
  [CONTENT_TAG_CATEGORIES_MAP.curriculum]: 20,
  [CONTENT_TAG_CATEGORIES_MAP.curriculumLevel]: 30,
  [CONTENT_TAG_CATEGORIES_MAP.curriculumLevelTwo]: 40,
  [CONTENT_TAG_CATEGORIES_MAP.theme]: 50,
  [CONTENT_TAG_CATEGORIES_MAP.subtheme]: 60,
  [CONTENT_TAG_CATEGORIES_MAP.misc]: 70,
  [CONTENT_TAG_CATEGORIES_MAP.meeoppTopic]: 80,
  [CONTENT_TAG_CATEGORIES_MAP.meeoppSubtopic]: 90,
  [CONTENT_TAG_CATEGORIES_MAP.meeoppCycleExerciseType]: 95,

  [CONTENT_TAG_CATEGORIES_MAP.questionMainTag1]: 100,
  [CONTENT_TAG_CATEGORIES_MAP.skillArea]: 110,
  [CONTENT_TAG_CATEGORIES_MAP.questionTopic]: 150,
  [CONTENT_TAG_CATEGORIES_MAP.questionResponseFormat]: 160,
  [CONTENT_TAG_CATEGORIES_MAP.questionExpressionMethod]: 170,

  [CONTENT_TAG_CATEGORIES_MAP.metadata]: 175,
  [CONTENT_TAG_CATEGORIES_MAP.relationKey]: 177,

  // Unused/Deprecated categories
  [CONTENT_TAG_CATEGORIES_MAP.questionCustom]: 180,
  [CONTENT_TAG_CATEGORIES_MAP.questionDifficulty]: 190,
  [CONTENT_TAG_CATEGORIES_MAP.difficulty]: 200,
  [CONTENT_TAG_CATEGORIES_MAP.topic]: 210,
  [CONTENT_TAG_CATEGORIES_MAP.type]: 220,
  [CONTENT_TAG_CATEGORIES_MAP.deprecated]: 230,
};

export const CONTENT_TAG_TITLES = Object.freeze({
  walkingGuidedTours: 'Walking Guided Tours',
});

export const CONTENT_TAG_CATEGORIES_LIST = Object.freeze(Object.values(CONTENT_TAG_CATEGORIES_MAP));
export const CONTENT_TAG_STATUS_LIST = Object.freeze(Object.values(CONTENT_TAG_STATUS_MAP));

export const QUESTION_ROOT_CONTENT_TAGS = Object.freeze([
  CONTENT_TAG_CATEGORIES_MAP.curriculum,
  CONTENT_TAG_CATEGORIES_MAP.questionCustom,
  CONTENT_TAG_CATEGORIES_MAP.questionMainTag1,
  CONTENT_TAG_CATEGORIES_MAP.skillArea,
  CONTENT_TAG_CATEGORIES_MAP.questionDifficulty,
  CONTENT_TAG_CATEGORIES_MAP.questionResponseFormat,
  CONTENT_TAG_CATEGORIES_MAP.questionExpressionMethod,
]);

// keep them in sync with backend
export const USER_ROLES_MAP = Object.freeze({
  videoRecordingAdmin: 'VIDEO_RECORDING_ADMIN',
  notesAdmin: 'NOTES_ADMIN',
  usersAdmin: 'USERS_ADMIN',
  usersLimitedRead: 'USERS_LIMITED_READ',
  topicsLimitedRead: 'TOPICS_LIMITED_READ',
  topicsAdmin: 'TOPICS_ADMIN',
  dailyActivityLogAdmin: 'DAILY_ACTIVITY_ADMIN',
  staffRolesAdmin: 'STAFF_ROLES_ADMIN',
  featureFlagsAdmin: 'FEATURE_FLAGS_ADMIN',
  pointsDashboardAdmin: 'POINTS_DASHBOARD_ADMIN',
  shortcutAdmin: 'SHORTCUT_ADMIN',
  shortcutModalAdmin: 'SHORTCUT_MODAL_ADMIN',
  contentTagFullAccessAdmin: 'CONTENT_TAG_FULL_ACCESS_ADMIN',
  faqAdmin: 'FAQ_ADMIN',
  customFontAdmin: 'CUSTOM_FONT_ADMIN',
  meeoppCoreAdmin: 'MEEOPP_CORE_ADMIN',
  libraryStatsAdmin: 'LIBRARY_STATS_ADMIN',
  analyticsAdmin: 'ANALYTICS_ADMIN',
  pointsAdmin: 'POINTS_ADMIN',
  avatarVideoAdmin: 'AVATAR_VIDEO_ADMIN',
  avatarSetsAdmin: 'AVATAR_SETS_ADMIN',
  meeoppCoreStatsAdmin: 'MEEOPP_CORE_STATS_ADMIN',
  emailAdmin: 'EMAIL_ADMIN',
  contentTagViewOnlyAdmin: 'CONTENT_TAG_VIEW_ONLY_ADMIN',
  groupBookingAdmin: 'GROUP_BOOKING_ADMIN',
  organizationAdmin: 'ORGANIZATION_ADMIN',
  flashcardAdmin: 'FLASHCARD_ADMIN',
  workLogAdmin: 'WORK_LOG_ADMIN',
  bundlesAdmin: 'BUNDLES_ADMIN',
  gameAdmin: 'GAME_ADMIN',
  passwordRequestAdmin: 'PASSWORD_REQUEST_ADMIN',
  passwordApproveAdmin: 'PASSWORD_APPROVE_ADMIN',
  challengeAnswerKeysAdmin: 'CHALLENGE_ANSWER_KEYS',
  pedaTeamMember: 'PEDA_TEAM_MEMBER',
  csTeamMember: 'CS_TEAM_MEMBER',

  // The vocab pack feature has been removed.
  vocabPackAdmin: 'VOCAB_PACK_ADMIN',
  oldVocabPacksAdmin: 'OLD_VOCAB_PACKS_ADMIN',
});

export const CONTENT_TAG_FOCUS_MAP = Object.freeze({
  listening: 'Listening',
  writing: 'Writing',
  oral: 'Oral',
  reading: 'Reading',
});

export const CONTENT_TAG_FOCUS_COLORS = Object.freeze({
  Oral: '#722ED1',
  Reading: '#52C41A',
  Listening: '#13C2C2',
  Writing: '#FD5463',
  Other: '#fc8080',
});

export const CONTENT_TAG_FOCUS_SORT_WEIGHTING = Object.freeze({
  [CONTENT_TAG_FOCUS_MAP.reading]: 0,
  [CONTENT_TAG_FOCUS_MAP.listening]: 1,
  [CONTENT_TAG_FOCUS_MAP.writing]: 2,
  [CONTENT_TAG_FOCUS_MAP.oral]: 3,
});

export const PREVIEW_MODAL_SIZE = Object.freeze({
  wide: 1200,
  narrow: 740,
});

export const QUESTION_TYPE_DISPLAY_NAME = Object.freeze({
  SHORT: 'Short Written Answer',
  MULTI_CHOICE: 'Multiple Choice',
  MULTI_ANSWER: 'Multiple Choice (Multiple Answers)',
  MULTI_ANSWER_ALPHABET: 'Multiple Choice (Fill in the Alphabet)',
  SPOKEN: 'Spoken Answer',
  MATCHING_PARENT: 'Matching Answer',
  MATCHING_CHILD: 'Matching Answer',
  TABLE_PARENT: 'Table Answer',
  TABLE_CHILD: 'Table Written Answer',
  TABLE_MULTI_PARENT: 'Table Answer',
  TABLE_MULTI_ROW_CHILD: 'Table Answer',
  MULTI_CHOICE_PLAYBOOK: 'Multiple Choice',
  MULTI_ANSWER_PLAYBOOK: 'Multiple Choice (Multiple Answers)',
  [QUESTION_TYPES_MAP.openEndedOral]: 'Oral (Open-ended)',
  [QUESTION_TYPES_MAP.openEndedWriting]: 'Written (Open-ended)',
});

export const MANUAL_GRADING_POSSIBLE = [
  QUESTION_TYPES_MAP.short,
  QUESTION_TYPES_MAP.spoken,
  QUESTION_TYPES_MAP.tableChild,
  QUESTION_TYPES_MAP.tableMulti,
  QUESTION_TYPES_MAP.tableMultiRowChild,
];

export const SHORT_ANSWER_TYPE_DISPLAY_NAME = Object.freeze({
  [SHORT_ANSWER_TYPES_MAP.oneLine]: '(One-line)',
  [SHORT_ANSWER_TYPES_MAP.trueFalse]: '(True / False)',
});

export const USER_ROLES = Object.freeze(Object.values(USER_ROLES_MAP));

export const YEAR_SYSTEMS = Object.freeze({
  UK: 'UK',
  HK: 'HK',
  US: 'US',
  SG: 'SG',
  UNIVERSITY: 'University',
});

function getYearVal(prefix, index) {
  const year = `${index + 1}`.padStart(2, '0');
  return `${prefix}_${year}`;
}

export const YEAR_NAMES = Object.freeze({
  [YEAR_SYSTEMS.UK]: [...new Array(13)].reduce((acc, value, index) => {
    acc[getYearVal('YEAR', index)] = `Year ${index + 1}`;
    return acc;
  }, {}),
  [YEAR_SYSTEMS.HK]: {
    ...[...new Array(6)].reduce((acc, value, index) => {
      acc[getYearVal('HK_PRIMARY', index)] = `Primary ${index + 1}`;
      return acc;
    }, {}),
    ...[...new Array(6)].reduce((acc, value, index) => {
      acc[getYearVal('HK_FORM', index)] = `Form ${index + 1}`;
      return acc;
    }, {}),
  },
  [YEAR_SYSTEMS.US]: {
    US_KINDERGARTEN: 'Kindergarten',
    ...[...new Array(12)].reduce((acc, value, index) => {
      acc[getYearVal('US_GRADE', index)] = `Grade ${index + 1}`;
      return acc;
    }, {}),
  },
  [YEAR_SYSTEMS.SG]: {
    ...[...new Array(6)].reduce((acc, value, index) => {
      acc[getYearVal('SG_PRIMARY', index)] = `Primary ${index + 1}`;
      return acc;
    }, {}),
    ...[...new Array(5)].reduce((acc, value, index) => {
      acc[getYearVal('SG_SECONDARY', index)] = `Secondary ${index + 1}`;
      return acc;
    }, {}),
  },
  [YEAR_SYSTEMS.UNIVERSITY]: {
    BACHELOR: 'Bachelor (Undergrad)',
    MASTER: 'Master (Postgrad)',
    DOCTORATE: 'Doctorate (PhD)',
  },
});

export const YEAR_DISPLAY_NAME_MAP = Object.freeze(
  Object.values(YEAR_NAMES).reduce(
    (acc, yearNames) => ({
      ...acc,
      ...yearNames,
    }),
    {}
  )
);

export const YEARS_BY_STAGE = Object.freeze({
  primary: Object.freeze(
    uniq([
      ...Object.keys(YEAR_NAMES[YEAR_SYSTEMS.UK]).slice(0, 6),
      ...Object.keys(YEAR_NAMES[YEAR_SYSTEMS.HK]).slice(0, 6),
      ...Object.keys(YEAR_NAMES[YEAR_SYSTEMS.SG]).slice(0, 6),
    ])
  ),
  secondary: Object.freeze(
    uniq([
      ...Object.keys(YEAR_NAMES[YEAR_SYSTEMS.UK]).slice(6, 13),
      ...Object.keys(YEAR_NAMES[YEAR_SYSTEMS.HK]).slice(6, 12),
      ...Object.keys(YEAR_NAMES[YEAR_SYSTEMS.SG]).slice(6, 11),
    ])
  ),
});

export const STAGE = {
  primary: 'primary',
  secondary: 'secondary',
  unknown: 'unknown',
};

export const FLASHCARD_CHINESE_CHAR_LIMIT = 40;
export const FLASHCARD_ENGLISH_CHAR_LIMIT = 100;
export const FLASHCARD_CUSTOM_CHAR_LIMIT = 220;
export const CONCEPT_CHINESE_DEFINITION_CHAR_LIMIT = 100;
export const CONCEPT_ENGLISH_DEFINITION_CHAR_LIMIT = 200;

export const POINT_EVENT_TYPES = {
  reviseAFlashcard: 'revise-a-flashcard',
  profilePictureUpload: 'profile-picture-upload',
  flashcardInSession: 'flashcard-in-session',
  questionBoxResponse: 'question-box-response',
  challengeQuestion: 'challenge-question',
  submitQuiz: 'submit-quiz',
  flashcardReviseStreak: 'flashcard-revise-streak',
};

export const DIFFICULTY_COLORS = {
  Easy: '#00AC62',
  'So-so': '#DDB836',
  Hard: '#F62121',
};

export const SELF_RATING_DESCRIPTION = {
  5: 'Easy',
  4: 'Easy',
  3: 'So-so',
  2: 'So-so',
  1: 'Hard',
  0: 'Hard',
};

export const CHALLENGE_BOOKING_TYPES = {
  default: 'DEFAULT',
  studentBooked: 'STUDENT_BOOKED',
};

export const LIBRARY_CONTENT_TYPES = {
  QUIZ: 'Challenge',
  PRESENTATION: 'Walking Guided Tour',
  WGT: 'Walking Guided Tour',
  ASSESSMENT: 'Assessment Challenge',
  CORE_PACK: 'Vocab Packs',
  PLAYBOOK: 'Playbook',
  // Arbitrary Type
  TOPIC: '1-1 Lesson',
};

//KEEP THIS UPDATED WITH USERS_ROLE_MAP
export const ROLES_TO_PAGES = [
  {
    name: 'Topic',
    role: ['TOPICS_ADMIN', 'TOPICS_LIMITED_READ'],
  },
  {
    name: 'Users',
    role: ['USERS_ADMIN', 'USERS_LIMITED_READ'],
  },
  {
    name: 'Email Admin',
    role: 'EMAIL_ADMIN',
  },
  {
    name: 'Staff Roles',
    role: 'STAFF_ROLES_ADMIN',
  },
  {
    name: 'FAQ',
    role: 'FAQ_ADMIN',
  },
  {
    name: 'Daily Activity',
    role: 'DAILY_ACTIVITY_ADMIN',
  },
  {
    name: 'Notes',
    role: 'NOTES_ADMIN',
  },
  {
    name: 'Feature Flags',
    role: 'FEATURE_FLAGS_ADMIN',
  },
  {
    name: 'Points Dashboard',
    role: 'POINTS_DASHBOARD_ADMIN',
  },
  {
    name: 'Content Tag Full Access',
    role: 'CONTENT_TAG_FULL_ACCESS_ADMIN',
  },
  {
    name: 'Content Tag View Only',
    role: 'CONTENT_TAG_VIEW_ONLY_ADMIN',
  },
  {
    name: 'Custom Font',
    role: 'CUSTOM_FONT_ADMIN',
  },
  {
    name: 'MeeOpp Core',
    role: 'MEEOPP_CORE_ADMIN',
  },
  {
    name: 'Vocab Pack',
    role: 'VOCAB_PACK_ADMIN',
  },
  {
    name: 'Library Stats',
    role: 'LIBRARY_STATS_ADMIN',
  },
  {
    name: 'Analytics',
    role: 'ANALYTICS_ADMIN',
  },
  {
    name: 'Points',
    role: 'POINTS_ADMIN',
  },
  {
    name: 'Avatar Video',
    role: 'AVATAR_VIDEO_ADMIN',
  },
  {
    name: 'Avatar Sets',
    role: 'AVATAR_SETS_ADMIN',
  },
  {
    name: 'MeeOpp Core Stats',
    role: 'MEEOPP_CORE_STATS_ADMIN',
  },
  // Old vocab packs has been removed
  {
    name: 'Old Vocab Packs',
    role: 'OLD_VOCAB_PACKS_ADMIN',
  },
  {
    name: 'Group Bookings',
    role: 'GROUP_BOOKING_ADMIN',
  },
  {
    name: 'Organizations',
    role: 'ORGANIZATION_ADMIN',
  },
  {
    name: 'Flashcards',
    role: 'FLASHCARD_ADMIN',
  },
  {
    name: 'Work Logs',
    role: 'WORK_LOG_ADMIN',
  },
  {
    name: 'Games',
    role: 'GAME_ADMIN',
  },
  {
    name: 'Password Request Admin',
    role: 'PASSWORD_REQUEST_ADMIN',
  },
  {
    name: 'Password Approve Admin',
    role: 'PASSWORD_APPROVE_ADMIN',
  },
];

export const MEECARD_DIFFICULTY_LABELS = {
  hard: 'hard',
  soSo: 'soSo',
};

export const WEB_ASSETS_URL = 'https://web.meeopp.com';

export const BUNDLE_MATERIAL_TYPES = ['CHALLENGE', 'IWGT', 'CORE_PACK', 'ONE_ON_ONE'];

export const BUNDLE_MATERIAL_NAMES = Object.freeze({
  CORE_PACK: 'Core Pack iWGT',
  PRESENTATION: 'iWGT',
  TOPIC: '1-1 Lesson',
  QUIZ: 'Challenge',
  ASSESSMENT: 'Challenge - Assessment',
});

export const QUESTION_SET_TYPE_MAP = Object.freeze({
  english: 'English Set',
  chinese: 'Chinese Set',
});

export const ORG_BOOKING_QUIZ_STATUSES = Object.freeze({
  expired: 'Expired',
  cancelled: 'Cancelled',
  scheduled: 'Scheduled',
  inProgress: 'In Progress',
  deadlinePassed: 'Deadline Passed',
});

export const TEACHING_LANGUAGES = {
  mandarin: {
    title: 'Mandarin',
    value: 'MANDARIN',
  },
  cantonese: {
    title: 'Cantonese',
    value: 'CANTONESE',
  },
  english: {
    title: 'English',
    value: 'ENGLISH',
  },
};

export const LEARNING_SUBJECT = {
  chineseMandarin: {
    title: 'Chinese (Mandarin)',
    value: 'CHINESE_MANDARIN',
  },
  chineseCantonese: {
    title: 'Chinese (Cantonese)',
    value: 'CHINESE_CANTONESE',
  },
  englishUK: {
    title: 'English (British)',
    value: 'ENGLISH_UK',
  },
  englishUS: {
    title: 'English (American)',
    value: 'ENGLISH_US',
  },
  psychology: {
    title: 'Psychology',
    value: 'PSYCHOLOGY',
  },
};

export const LEARNING_SUBJECT_TO_DISPLAY_SIMPLE = Object.freeze(
  Object.values(LEARNING_SUBJECT).reduce((mapping, cfg) => {
    mapping[cfg.value] = cfg.simpleTitle;
    return mapping;
  }, {})
);

export const LEARNING_SUBJECT_MAP_TITLE_BY_VALUE = Object.freeze({
  [LEARNING_SUBJECT.chineseMandarin.value]: LEARNING_SUBJECT.chineseMandarin.title,
  [LEARNING_SUBJECT.chineseCantonese.value]: LEARNING_SUBJECT.chineseCantonese.title,
  [LEARNING_SUBJECT.englishUK.value]: LEARNING_SUBJECT.englishUK.title,
  [LEARNING_SUBJECT.englishUS.value]: LEARNING_SUBJECT.englishUS.title,
});

export const LEARNING_SUBJECT_TYPES = Object.freeze(
  Object.values(LEARNING_SUBJECT).map((subject) => subject.value)
);

export const USER_COMMS_LANGUAGE_CODE = {
  CHINESE: 'cn',
  ENGLISH: 'en',
};

export const LEARNING_SUBJECT_TO_DISPLAY = Object.freeze(
  Object.values(LEARNING_SUBJECT).reduce((mapping, cfg) => {
    mapping[cfg.value] = cfg.title;
    return mapping;
  }, {})
);

export const QUIZ_BOOKING_MODES = Object.freeze({
  practice: 'PRACTICE',
  assessment: 'ASSESSMENT',
});

export const BOOKING_FREQUENCY = Object.freeze({
  daily: 5,
  threePerWeek: 3,
  twicePerWeek: 2,
  oncePerWeek: 1,
  sameDate: 0,
});

export const USER_SUBJECTS = Object.freeze({
  general: 'GENERAL',
  mandarin: 'MANDARIN',
});

export const SOLO_LESSON_TYPES = Object.freeze([
  LESSON_TYPES.SOLO,
  LESSON_TYPES.WGT,
  LESSON_TYPES.CORE_ITEMS_IWGT,
  LESSON_TYPES.QUIZ,
  LESSON_TYPES.PLAYBOOK,
]);

const MATERIAL_CONTENT_CATEGORIES = [
  '古詩解析',
  '名人故事',
  '應用文',
  '成語故事',
  '神話故事',
  '記敍文',
  '議論文',
  '説明文',
  '描寫文',
  '抒情文',
  '文言文',
  '劇本',
  '詞語',
  '句型',
  '標點',
  '修辭',
  '小説',
];

export const PRESENTATION_CONTENT_CATEGORIES = MATERIAL_CONTENT_CATEGORIES;
export const QUIZ_CONTENT_CATEGORIES = MATERIAL_CONTENT_CATEGORIES;

export const WELCOME_EMAIL_LANGUAGE = {
  ENG: {
    label: 'ENGLISH',
    title: 'English',
  },
  TRA_CHI: {
    label: 'TRADITIONAL_CHINESE',
    title: 'Traditional Chinese',
  },
};

export const LOGIN_URL_PARAMS_TO_FORWARD = Object.freeze([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'type',
  'id',
  // Various Reports
  'sessionTypes',
  'learningActions',
  'dateRangePreset',
  'showComposition',
  'showDateLabel',
  'showFutureDates',
  'unansweredQAsIncorrect',
  'stage',
  'startDate',
  'endDate',
  'sortBy',
  'sortOrder',
  'timezone',
  'showNumbers',
  'includeSelfStudy',
  'includeArchivedClasses',
  'chart',
  'learningSubjects',
  'yearLevel',
  'subject',
  'intervalId',
  'academicYear',
  'lessonPlanId',
  'activityType',
  // Booking request page
  'tabKey',
  'showProcessed',
  'organizationId',
  'participantId',
  'requestStatuses',
  // Users filter page --> organizationId used as well
  'userType',
  'searchText',
  'page',
  // Assessment Report
  'materialIds',
  'fieldNames',
  'sections',
  // Admin Content Filters
  'showEditModal',
  'scrollTo',
  // Mark Questions
  'schoolClassId',
  'studentId',
  // Library Filters
  'contentTagIds',
  'contentType',
  'textTypes',
  'curriculumIds',
  'themeIds',
  'focus',
  'viewAsUserId',
  'bundleId',
  'bundles',
  'estimatedDurations',
  'searchKeyword',
  'classId',
  'withPracticeBuddy',
  'gradingOptionIds',
  // New Booking Details
  'useOldBookingDetails',
  // Class Page
  'topic',
  'bookingType',
  'showAssessmentsOnly',
  'showInClassLeaderboardModal',
  // Student Reviews
  'showCommentedReviewsOnly',
]);

export const LIBRARY_CONTENT_CONTENT_TYPE = {
  quiz: 'QUIZ',
  topic: 'TOPIC',
  presentation: 'PRESENTATION',
  assessment: 'ASSESSMENT',
  corePack: 'CORE_PACK',
  playbook: 'PLAYBOOK',
};

export const ORGANIZATION_TYPE = {
  local: 'LOCAL',
  international: 'INTERNATIONAL',
  university: 'UNIVERSITY',
};

export const GRADING_STATUSES = Object.freeze({
  notGraded: 'NOT_GRADED',
  gradingStarted: 'GRADING_STARTED',
  gradingComplete: 'GRADING_COMPLETE',
});

export const CLASS_LESSON_PERFORMANCE_SESSION_TYPES = Object.freeze({
  wgt: 'WGT',
  challenge: 'CHALLENGE',
  assessment: 'ASSESSMENT',
});

export const USER_POINTS_EVENTS = Object.freeze({
  challengeQuestion: 'challenge-question',
  questionBoxResponse: 'question-box-response',
  flashcardInSession: 'flashcard-in-session',
  reviseAFlashCard: 'revise-a-flashcard',
  submitQuiz: 'submit-quiz',
  completeIWGT: 'complete-iwgt',
  completeCoreItemsIWGTLesson: 'complete-core-items-iwgt-lesson',
  completeLesson: 'complete-lesson',
  startGame: 'start-game',
});

export const USER_POINTS_EVENTS_DISPLAY_NAME_MAP = Object.freeze({
  [USER_POINTS_EVENTS.challengeQuestion]: 'Challenge/Assessment Questions Answered',
  [USER_POINTS_EVENTS.questionBoxResponse]: 'iWGT Questions Answered',
  [USER_POINTS_EVENTS.flashcardInSession]: 'MeeCards Answered',
  [USER_POINTS_EVENTS.reviseAFlashCard]: 'MeeCards Revised',
  [USER_POINTS_EVENTS.submitQuiz]: 'Challenges/Assessments Submitted',
  [USER_POINTS_EVENTS.completeIWGT]: 'iWGTs Completed',
  [USER_POINTS_EVENTS.completeCoreItemsIWGTLesson]: 'Core Items Completed',
  [USER_POINTS_EVENTS.completeLesson]: '1-on-1 Sessions',
  [USER_POINTS_EVENTS.startGame]: 'Games Started',
});

export const SOLO_LESSON_TYPES_WITH_LABEL = [
  {
    label: 'Challenges',
    value: CONTENT_TYPE.quiz,
  },
  {
    label: 'Assessments',
    value: CONTENT_TYPE.assessment,
  },
  {
    label: 'Walking Guided Tours',
    value: CONTENT_TYPE.presentation,
  },
  {
    label: 'Vocab Packs',
    value: CONTENT_TYPE.corePack,
  },
  {
    label: 'Playbooks',
    value: CONTENT_TYPE.playbook,
  },
];

export const CONTENT_TYPES_WITH_LABEL = [
  ...SOLO_LESSON_TYPES_WITH_LABEL,
  {
    label: '1-on-1',
    value: CONTENT_TYPE.topic,
  },
];

export const contentTypeLabelByValue = (value) => {
  return CONTENT_TYPES_WITH_LABEL.find((c) => c.value === value).label;
};

export const MEEOPP_LEVEL_MIN = 0;
export const MEEOPP_LEVEL_MAX = 13;

export const PLAYBOOK_SCOPES_MAP = {
  vocabulary: 'VOCABULARY',
  grammar: 'GRAMMAR',
  passage: 'PASSAGE',
};

export const CONTENT_SHOW_TO_LIBRARY_STATUS = {
  showToVisibleOrgs: 'SHOW_TO_VISIBLE_ORGS',
  showToMeeOppOnly: 'SHOW_TO_MEEOPP_ONLY',
};

export const LOGIN_METHODS = {
  GOOGLE: 'google',
  EMAIL: 'email',
};

export const LOGIN_ORIGIN = 'net';

export const ADD_STUDENTS_TO_CLASS_PAGES = Object.freeze({
  CLASS_CREATION: 'class creation',
  ROSTER_MANAGEMENT: 'roster management',
});

export const ADD_STUDENTS_TO_CLASS_INPUT_METHODS = Object.freeze({
  CSV: 'csv',
  FORM: 'form',
});

export const QUIZ_AUDIO_CLIPS_POSITION = Object.freeze({
  top: 'TOP',
  bottom: 'BOTTOM',
});

export const STUDENT_BOOKING_PRACTICE_TYPES = Object.freeze({
  speaking: {
    value: 'SPEAKING',
    label: 'Speaking',
  },
  writing: {
    value: 'WRITING',
    label: 'Writing',
  },
  listening: {
    value: 'LISTENING',
    label: 'Listening',
  },
  reading: {
    value: 'READING',
    label: 'Reading',
  },
  revision: {
    value: 'REVISION',
    label: 'Revision',
  },
});

export const ESTIMATED_DURATION_CATEGORY = Object.freeze({
  long: 'LONG',
  veryLong: 'VERY_LONG',
  short: 'SHORT',
});

export const DURATION_CATEGORY_DISPLAY_MAP = Object.freeze({
  [ESTIMATED_DURATION_CATEGORY.short]: '< 5 minutes',
  [ESTIMATED_DURATION_CATEGORY.long]: '5-10 minutes',
  [ESTIMATED_DURATION_CATEGORY.veryLong]: '> 10 minutes',
});

export const STUDENT_GROUP_NAME_MAX_LENGTH = 20;

export const BOOKING_CART_ACCEPTABLE_CONTENT_TYPES = [
  CONTENT_TYPE.quiz,
  CONTENT_TYPE.presentation,
  CONTENT_TYPE.assessment,
  CONTENT_TYPE.corePack,
];

export const REPORT_INTERVAL_PRESET = Object.freeze({
  allTime: {
    name: 'All Time',
    id: 'all-time',
  },
  last7Days: {
    name: 'Last 7 Days',
    id: 'last-7-days',
  },
  last30Days: {
    name: 'Last 30 Days',
    id: 'last-30-days',
  },
  last90Days: {
    name: 'Last 90 Days',
    id: 'last-90-days',
  },
  last6Months: {
    name: 'Last 6 Months',
    id: 'last-6-months',
  },
});

export const CONTENT_REF_TYPES = Object.freeze({
  writingCheckpoint: 'WRITING_CHECKPOINT',
});
